import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Row, Col, Form, Container } from "react-bootstrap";
import { connect } from "react-redux";
import { isEmpty } from "lodash";
import { getRestaurantData } from "../AddResturant/Resturant.action";
import { AddReceivingApi } from "./Receiving.action";
import { GlobalSpinner, roundToTwoDecimals } from "../../../../../global/globalFunctions";
import { getVendorData } from "../Vendor/Vendor.action";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import Select from "react-select";
import Swal from "sweetalert";
import moment from "moment";
import SendIcon from "@mui/icons-material/Send";
import Button from "@mui/material/Button";
import { setHeading } from "../../../accounts/account.actions";
import { getProductData, GetProductTypeByIdApi } from "../AddProduct/Product.action";

// Empty input field template
const EMPTY_INPUT_FIELD = {
  productId: 0,
  productVariationId: null,
  productName: "",
  productVariationLabel: "",
  salePrice: 0,
  purchasePrice: 0,
  requiredProductQuantity: 0,
  productTypeName: "",
  availableProductQuantity: 0,
};

const AddReceiving = (props) => {
  const [inputField, setInputField] = useState([{ ...EMPTY_INPUT_FIELD }]);
  const [restaurantId, setRestaurantId] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [receivedBy, setReceivedBy] = useState("");
  const [description, setDescription] = useState("");
  const [discount, setDiscount] = useState(0);
  const [leakDiscount, setLeakDiscount] = useState(0);
  const [adjustment, setAdjustment] = useState(0);

  // --- Derived / memoized lists ---
  const restaurantList = useMemo(
    () =>
      props.restaurantDataList?.map((x) => ({
        value: x._id,
        label: x.restaurantName,
      })) ?? [],
    [props.restaurantDataList]
  );

  const vendorList = useMemo(
    () =>
      props.vendorDataList?.map((x) => ({
        value: x._id,
        label: x.vendorName,
      })) ?? [],
    [props.vendorDataList]
  );

  const productList = useMemo(
    () =>
      props.productDataList.map((x) => ({
        value: x._id,
        label: x.name,
        productBalance: x.productBalance,
        salePrice: x.salePrice,
        purchasePrice: x.purchasePrice,
        productTypeName: x.productTypeName,
      })),
    [props.productDataList]
  );

  const variationList = useMemo(
    () =>
      props.productTypeListById?.[0]?.variation?.map((x) => ({
        value: x._id,
        label: `${x.colourLabel} / ${x.sizeLabel}`,
        purchasePrice: x.purchasePrice,
        sellingPrice: x.sellingPrice,
        availableProductQuantity: x.productBalance
      })) ?? [],
    [props.productTypeListById]
  );

  const receivingTypes = [
    { value: 0, label: 'Carton' },
    { value: 1, label: 'Piece' }
  ];
  const receivingTypesList = useMemo(() =>
    receivingTypes.map((x) => ({
      value: x.value,
      label: x.label
    })),
    []
  );

  const totalBill = useMemo(() => {
    const sum = inputField.reduce((acc, item) => {
      return acc + (item.purchasePrice * item.requiredProductQuantity);
    }, 0);
    return roundToTwoDecimals(sum);
  }, [inputField]);

  // const subtotal = useMemo(() => {
  //   return roundToTwoDecimals(totalBill - Number(discount) - Number(leakDiscount));
  // }, [totalBill, discount, leakDiscount]);


  const totalDiscount = useMemo(() => {
    return roundToTwoDecimals(Number(discount) + Number(leakDiscount));
  }, [discount, leakDiscount]);

  const tax = useMemo(() => {
    return roundToTwoDecimals(totalBill * 0.1 / 100);  // was valueAfterDiscount
  }, [totalBill]);

  const netPayable = useMemo(() => {
    return roundToTwoDecimals(totalBill - totalDiscount + tax - Number(adjustment));
    // was: valueAfterDiscount + tax - adjustment
  }, [totalBill, totalDiscount, tax, adjustment]);


  // --- Handlers ---
  const handleChangeRestaurant = useCallback((selected) => {
    setRestaurantId(selected.value);
    setRestaurantName(selected.label);
  }, []);

  const handleChangeVendor = useCallback((selected) => {
    setVendorId(selected.value);
    setVendorName(selected.label);
  }, []);

  const handleChange = useCallback(
    (e, index, type) => {
      setInputField((prev) => {
        const list = [...prev];
        const row = { ...list[index] };

        if (type === "master") {
          row.productId = e.value;
          row.productName = e.label;
          row.availableProductQuantity = e.productBalance ?? 0;
          row.salePrice = roundToTwoDecimals(e.salePrice ?? 0);
          row.purchasePrice = roundToTwoDecimals(e.purchasePrice ?? 0);
          row.productTypeName = e.productTypeName ?? "";
          // Reset variation & quantity when product changes
          row.productVariationId = null;
          row.productVariationLabel = "";
          row.requiredProductQuantity = 0;
          // Fetch variations if needed
          if (e.productTypeName === "Variation Product") {
            props.GetProductTypeByIdApi(e.value);
          }
        }

        if (type === "variation") {
          row.productVariationId = e.value;
          row.productVariationLabel = e.label;
          row.purchasePrice = roundToTwoDecimals(e.purchasePrice ?? 0);
          row.availableProductQuantity = e.availableProductQuantity ?? 0;
          row.salePrice = roundToTwoDecimals(e.sellingPrice ?? 0);
        }

        if (type === "intquantity") {
          const qty = Number(e.target.value);
          row.requiredProductQuantity = qty < 0 ? 0 : qty;
          if (qty < 0) alert("Quantity cannot be less than 0");
        }

        if (type === "purchasePrice") {
          const price = Number(e.target.value);
          row.purchasePrice = price < 0 ? 0 : roundToTwoDecimals(price);
        }

        list[index] = row;
        return list;
      });
    },
    [props.GetProductTypeByIdApi]
  );

  const handleAddInput = useCallback(() => {
    setInputField((prev) => [...prev, { ...EMPTY_INPUT_FIELD }]);
  }, []);

  const handleRemoveInput = useCallback((index) => {
    setInputField((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleVisible = useCallback((msg) => {
    Swal({ title: "Success", text: msg, icon: "success", button: "Ok" });
  }, []);

  const handleDangerVisible = useCallback((err) => {
    Swal({ title: "Error", text: err, icon: "error", button: "Ok" });
  }, []);

  const resetForm = useCallback(() => {
    setRestaurantId("");
    setRestaurantName("");
    setVendorId("");
    setVendorName("");
    setDate(moment().format("YYYY-MM-DD"));
    setReceivedBy("");
    setDescription("");
    setDiscount(0);
    setLeakDiscount(0);
    setAdjustment(0);
    setInputField([{ ...EMPTY_INPUT_FIELD }]);
  }, []);

  const saveReceiving = useCallback(() => {
    if (!inputField.length) {
      return handleDangerVisible("Please add at least one valid product");
    }

    // Round numeric values in each row before sending to backend
    const normalizedReceivingData = inputField.map((item) => ({
      ...item,
      salePrice: roundToTwoDecimals(Number(item.salePrice) || 0),
      purchasePrice: roundToTwoDecimals(Number(item.purchasePrice) || 0),
      requiredProductQuantity: roundToTwoDecimals(Number(item.requiredProductQuantity) || 0),
      availableProductQuantity: roundToTwoDecimals(Number(item.availableProductQuantity) || 0),
    }));

    const payload = {
      restaurantId,
      restaurantName,
      receivingDate: date,
      receivedBy: receivedBy.trim(),
      recivingDescription: description.trim(),
      totalBill: roundToTwoDecimals(Number(totalBill) || 0),
      discount: roundToTwoDecimals(Number(discount) || 0),
      leakDiscount: roundToTwoDecimals(Number(leakDiscount) || 0),
      tax: roundToTwoDecimals(Number(tax) || 0),
      adjustment: roundToTwoDecimals(Number(adjustment) || 0),
      netPayable: roundToTwoDecimals(Number(netPayable) || 0),
      vendorDetails: {
        vendorId,
        vendorName,
      },
      receivingData: normalizedReceivingData,
    };

    props.AddReceivingApi(
      payload,
      (msg) => {
        handleVisible(msg);
        resetForm();
      },
      handleDangerVisible
    );

  }, [
    restaurantId,
    restaurantName,
    vendorId,
    vendorName,
    date,
    receivedBy,
    description,
    discount,
    leakDiscount,
    tax,
    totalBill,
    adjustment,
    netPayable,
    inputField,
    handleVisible,
    handleDangerVisible,
    resetForm
  ]);

  const isSaveDisabled =
    isEmpty(restaurantId) ||
    isEmpty(vendorId) ||
    isEmpty(receivedBy) ||
    inputField.some((f) => isEmpty(f.productName) || f.requiredProductQuantity <= 0);

  useEffect(() => {
    props.setHeading("New Receiving");
    props.getRestaurantData();
    props.getVendorData();
    props.getProductData();
  }, []);

  return (
    <>
      {props.isAddingReceiving || props.isFetchingRestaurant || props.isFetchingProduct ? (
        <GlobalSpinner />
      ) : (
        <Container fluid>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Date *</b></Form.Label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(moment(e.target.value).format("YYYY-MM-DD"))}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Select Company *</b></Form.Label>
                <Select
                  isDisabled={isEmpty(restaurantList)}
                  placeholder="Select Company"
                  onChange={handleChangeRestaurant}
                  options={restaurantList}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Select Vendor *</b></Form.Label>
                <Select
                  placeholder="Select Vendor"
                  onChange={handleChangeVendor}
                  options={vendorList}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Received By *</b></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Received By"
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Description</b></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="garana">
                <Form.Label><b>Select Receiving Type *</b></Form.Label>
                <Select
                  placeholder="Select Receiving Type"
                  // onChange={(option) => handleFormChange('receivingTypeId', option.value)}
                  options={receivingTypesList}
                />
              </Form.Group>
            </Col>
          </Row>

          {inputField.map((item, i) => (
            <React.Fragment key={i}>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><b>Select Product *</b></Form.Label>
                    <Select
                      value={item.productName ? { label: item.productName, value: item.productId } : null}
                      placeholder="Select Product"
                      onChange={(e) => handleChange(e, i, "master")}
                      options={productList}
                      isOptionDisabled={(option) => {
                        const alreadySelected = inputField.some((x) => x.productId === option.value);
                        // Find product from list
                        const product = productList.find((p) => p.value === option.value);
                        // Disable only if:
                        // 1) already selected
                        // 2) AND it's NOT a variation product
                        return (
                          alreadySelected &&
                          product?.productTypeName !== "Variation Product"
                        );
                      }}
                    />
                  </Form.Group>
                </Col>

                {item.productTypeName === "Variation Product" ? (
                  <>
                    <Col>
                      <Form.Group>
                        <Form.Label><b>Select Variation *</b></Form.Label>
                        <Select
                          isDisabled={isEmpty(variationList)}
                          placeholder="Select Variation"
                          value={item.productVariationId ? { label: item.productVariationLabel, value: item.productVariationId } : null}
                          onChange={(e) => handleChange(e, i, "variation")}
                          options={variationList}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label><b>Purchase Price</b></Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Purchase Price"
                          value={item.purchasePrice}
                          min={0}
                          onChange={(e) => handleChange(e, i, "purchasePrice")}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group className="mb-3">
                        <Form.Label><b>Add Quantity *</b></Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="Add Quantity"
                          value={item.requiredProductQuantity}
                          min={0}
                          onChange={(e) => handleChange(e, i, "intquantity")}
                        />
                      </Form.Group>
                    </Col>
                    <Col style={{ marginTop: "30px" }}>
                      {inputField.length - 1 == i && (
                        <Button
                          title="Click to Add Product"
                          onClick={handleAddInput}
                          style={{
                            float: "right",
                            color: "white",
                            backgroundColor: '#81ABD3',
                          }}
                        >
                          <AddIcon />
                        </Button>
                      )}

                      {inputField.length !== 1 && (
                        <Button
                          title="Click to Remove Product"
                          onClick={() => handleRemoveInput(i)}
                          style={{
                            float: "right",
                            color: "white",
                            backgroundColor: '#81ABD3',
                            marginRight: "10px",
                          }}
                        >
                          <RemoveIcon />
                        </Button>
                      )}
                    </Col>
                  </>
                ) : (
                  item.productName && (
                    <>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label><b>Available Quantity</b></Form.Label>
                          <Form.Control
                            placeholder="Available Quantity"
                            disabled
                            value={roundToTwoDecimals(item.availableProductQuantity)}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label><b>Purchase Price</b></Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Purchase Price"
                            value={item.purchasePrice}
                            min={0}
                            onChange={(e) => handleChange(e, i, "purchasePrice")}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label><b>Add Quantity *</b></Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="Add Quantity"
                            value={item.requiredProductQuantity}
                            min={0}
                            onChange={(e) => handleChange(e, i, "intquantity")}
                          />
                        </Form.Group>
                      </Col>
                      <Col style={{ marginTop: "30px" }}>
                        {inputField.length - 1 == i && (
                          <Button
                            title="Click to Add Invoice"
                            onClick={handleAddInput}
                            style={{
                              float: "right",
                              color: "white",
                              backgroundColor: '#81ABD3',
                            }}
                          >
                            <AddIcon />
                          </Button>
                        )}

                        {inputField.length !== 1 && (
                          <Button
                            title="Click to Remove Invoice"
                            onClick={() => handleRemoveInput(i)}
                            style={{
                              float: "right",
                              color: "white",
                              backgroundColor: '#81ABD3',
                              marginRight: "10px",
                            }}
                          >
                            <RemoveIcon />
                          </Button>
                        )}
                      </Col>
                    </>
                  )
                )}
              </Row>

            </React.Fragment>
          ))}

          <Row style={{ marginTop: '30px' }} className="g-3">
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Total Bill</b></Form.Label>
                <Form.Control
                  value={roundToTwoDecimals(totalBill)}
                  disabled
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Discount</b></Form.Label>
                <Form.Control
                  type="number"
                  value={discount}
                  min={0}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 0;
                    const maxAllowed = totalBill - Number(leakDiscount);
                    setDiscount(roundToTwoDecimals(val > maxAllowed ? maxAllowed : val));
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Leak Discount</b></Form.Label>
                <Form.Control
                  type="number"
                  value={leakDiscount}
                  min={0}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 0;
                    const maxAllowed = totalBill - Number(discount);
                    setLeakDiscount(roundToTwoDecimals(val > maxAllowed ? maxAllowed : val));
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Tax (0.1%)</b></Form.Label>
                <Form.Control
                  value={roundToTwoDecimals(tax)}
                  disabled
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Adjustment</b></Form.Label>
                <Form.Control
                  type="number"
                  value={adjustment}
                  min={0}
                  onChange={(e) => {
                    const val = Number(e.target.value) || 0;
                    setAdjustment(roundToTwoDecimals(val < 0 ? 0 : val));
                  }}
                />
              </Form.Group>
            </Col>
            <Col xs={6} sm={4} md={3} lg>
              <Form.Group className="mb-3">
                <Form.Label><b>Net Payable Bill</b></Form.Label>
                <Form.Control
                  value={roundToTwoDecimals(netPayable)}
                  disabled
                  style={{ fontWeight: 'bold', backgroundColor: '#e8f4fd' }}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* ── Save button ── */}
          <div className="sendDiv" style={{ marginTop: "1rem" }}>
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              size="large"
              disabled={isSaveDisabled}
              onClick={saveReceiving}
            >
              SAVE
            </Button>
          </div>

        </Container>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  AddReceivingApi: (data, onSuccess, onError) => dispatch(AddReceivingApi(data, onSuccess, onError)),
  setHeading: (data) => dispatch(setHeading(data)),
  getRestaurantData: () => dispatch(getRestaurantData()),
  getVendorData: () => dispatch(getVendorData()),
  getProductData: () => dispatch(getProductData()),
  GetProductTypeByIdApi: (data) => dispatch(GetProductTypeByIdApi(data)),
});

const mapStateToProps = (state) => ({
  isAddingReceiving: state.RecivingReducer.isAddingReceiving,
  isFetchingVendor: state.VendorReducer.isFetchingVendor,
  vendorDataList: state.VendorReducer.vendorDataList,
  isFetchingRestaurant: state.ResturantReducer.isFetchingRestaurant,
  restaurantDataList: state.ResturantReducer.restaurantDataList,
  isFetchingProduct: state.ProductReducer.isFetchingProduct,
  productDataList: state.ProductReducer.productDataList,
  productTypeListById: state.ProductReducer.productTypeListById,
});

export default connect(mapStateToProps, mapDispatchToProps)(AddReceiving);